export type NotificationPriority = 'low' | 'normal' | 'high' | 'critical';

export type NotificationCategory =
  | 'vulnerabilities'
  | 'assets'
  | 'scans'
  | 'reports'
  | 'tasks'
  | 'brand'
  | 'organization'
  | 'system';

export interface NotificationMetadata {
  name?: string;
  comment?: string;
  author?: string;
  user_email?: string;
  [key: string]: unknown;
}

export interface NotificationAction {
  label: string;
  url: string;
}

export interface Notification {
  id: string;
  event_type: string;
  category: string;
  priority: NotificationPriority;
  title: string;
  body: string;
  entity_type?: string;
  entity_id?: string;
  action?: NotificationAction;
  group_count: number;
  /** null = непрочитано */
  read_at: string | null;
  metadata?: NotificationMetadata | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface UnreadCountResponse {
  total: number;
  notifications_total: number;
  by_category: {
    [key: string]: number;
  };
  by_priority: {
    [key: string]: number;
  };
  last_notifications: Notification[];
}

export interface SendTestNotificationResponse {
  message: string;
}

export interface NotificationsParams {
  page?: number;
  limit?: number;
  sort_by?: 'newest' | 'oldest';
  read?: 'all' | 'read' | 'unread';
  category?: NotificationCategory;
  event_type?: string;
  priority?: NotificationPriority;
  /** RFC3339 */
  date_from?: string;
  /** RFC3339 */
  date_to?: string;
  query?: string;
}

export interface NotificationsReadMultipleRequest {
  ids: string[];
}

export type NotificationChannel = 'web' | 'email' | 'telegram' | 'max';

export type EmailBatchingFrequency = 'immediately' | 'hourly' | 'daily' | 'weekly';

export interface NotificationEventSettings {
  event_type: string;
  name: string;
  channels: Record<NotificationChannel, boolean>;
  /** Событие нельзя отключить (переключатели заблокированы) */
  mandatory: boolean;
}

export interface NotificationCategorySettings {
  id: string;
  name: string;
  events: NotificationEventSettings[];
}

export interface NotificationProfileSettings {
  email_frequency: EmailBatchingFrequency;
  sound_enabled: boolean;
}

export type OpenPortAlertMode = 'all_except_80_443' | 'selected' | 'all';

export interface OpenPortRange {
  start: number;
  end: number;
}

export interface DangerousPortSetting {
  port: number;
  service: string;
  risk_explanation: string;
  service_en?: string;
  risk_explanation_en?: string;
  enabled: boolean;
}

export interface OpenPortAlertingSettings {
  mode: OpenPortAlertMode;
  selected_ranges: OpenPortRange[];
  disabled_dangerous_ports: number[];
  dangerous_ports: DangerousPortSetting[];
}

export interface NotificationSettings {
  categories: NotificationCategorySettings[];
  profile: NotificationProfileSettings;
  telegram_connected: boolean;
  max_connected: boolean;
  open_port_alerting: OpenPortAlertingSettings;
}

export interface NotificationPreferenceUpdate {
  event_type: string;
  channel: NotificationChannel;
  enabled: boolean;
}

export interface NotificationSettingsUpdateRequest {
  profile?: Partial<NotificationProfileSettings>;
  preferences?: NotificationPreferenceUpdate[];
  open_port_alerting?: OpenPortAlertingSettings;
}

export interface TelegramConnectResponse {
  token: string;
  bot_url: string;
  qr_code: string;
}

export interface TelegramStatusResponse {
  connected: boolean;
  telegram_id?: number;
  username?: string;
  connected_at?: string;
}

export interface MaxConnectResponse {
  token: string;
  bot_url: string;
  qr_code: string;
}

export interface MaxStatusResponse {
  connected: boolean;
  max_user_id?: number;
  username?: string;
  connected_at?: string;
}

/** Уведомление непрочитано */
export const isUnread = (notification: Pick<Notification, 'read_at'>): boolean =>
  notification.read_at == null;
